import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Autocomplete,
  Box,
  Chip,
  InputAdornment,
  Typography,
  Snackbar,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

interface EmployeeOption {
  empCode: number;
  empName: string;
  emailId?: string;
}

interface AddEmployeeModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (employees: EmployeeOption[]) => void;
  employees: EmployeeOption[];
}

const AddEmployeeModal: React.FC<AddEmployeeModalProps> = ({
  open,
  onClose,
  onCreate,
  employees
}) => {
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeOption[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    if (!open) {
      setSelectedEmployees([]);
    }
  }, [open]);

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    onClose();
  };

  const handleCreate = () => {
    if (selectedEmployees.length === 0) return;
    onCreate(selectedEmployees);
    setSelectedEmployees([]);
    setSnackbar({ open: true, message: 'New employee added successfully', severity: 'success' });
  };

  const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          <span className="app-snackbar-message">{snackbar.message}</span>
        </Alert>
      </Snackbar>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: '1.1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          Add Employee
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 500, mb: 1 }}>
              Employee Name <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Autocomplete
              multiple
              options={employees}
              value={selectedEmployees}
              onChange={(_, newValue) => setSelectedEmployees(newValue)}
              getOptionLabel={(option) =>
                `${option.empCode} - ${option.empName}`
              }
              isOptionEqualToValue={(option, value) => option.empCode === value.empCode}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const tagProps = getTagProps({ index });
                  const { key: _keyProp, ...restProps } = tagProps as any;
                  return (
                    <Chip
                      key={option.empCode}
                      label={`${option.empCode} - ${option.empName}`}
                      {...restProps}
                      sx={{
                        backgroundColor: '#e3edf7',
                        color: '#124b84',
                        fontWeight: 500,
                        borderRadius: '16px'
                      }}
                    />
                  );
                })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Search employee"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ pr: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={handleCreate}
            sx={{
              backgroundColor: '#124B84',
              textTransform: 'none',
              fontWeight: 500,
              px: 3,
              '&:hover': { backgroundColor: '#0f3f70' },
              '&:disabled': { backgroundColor: '#bdbdbd' },
            }}
          >
            Create
          </Button>
          <Button
            variant="contained"
            onClick={onClose}
            sx={{
              backgroundColor: '#e53935',
              textTransform: 'none',
              fontWeight: 500,
              px: 3
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddEmployeeModal;